/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import {extendTheme} from '@chakra-ui/react'

// Foundational style overrides
import styles from './foundations/styles'
import colors from './foundations/colors'
import gradients from './foundations/gradients'
import sizes from './foundations/sizes'
import space from './foundations/space'
import layerStyles from './foundations/layerStyles'
import shadows from './foundations/shadows'
import textStyles from './foundations/textStyles'
import typography from './foundations/typography'
import breakpoints from './foundations/breakpoints'
import animations from './foundations/animations'

// Base component style overrides
import Alert from './components/base/alert'
import Accordion from './components/base/accordion'
import Badge from './components/base/badge'
import Button from './components/base/button'
import Checkbox from './components/base/checkbox'
import CloseButton from './components/base/close-button'
import Container from './components/base/container'
import Divider from './components/base/divider'
import Drawer from './components/base/drawer'
import Form from './components/base/form'
import FormError from './components/base/formError'
import FormLabel from './components/base/formLabel'
import Icon from './components/base/icon'
import Input from './components/base/input'
import Textarea from './components/base/textarea'
import Link from './components/base/link'
import List from './components/base/list'
import Menu from './components/base/menu'
import Modal from './components/base/modal'
import Radio from './components/base/radio'
import Select from './components/base/select'
import Skeleton from './components/base/skeleton'
import SkipLink from './components/base/skip-link'
import Stepper from './components/base/stepper'
import Tabs from './components/base/tabs'
import Tooltip from './components/base/tooltip'
import Popover from './components/base/popover'
import Progress from './components/base/progress'
import Video from './components/base/video'

// Project Component style overrides
import Adyen from './components/project/adyen'
import App from './components/project/_app'
import Chip from './components/project/chip'
import ColorsIndicator from './components/project/colors-indicator'
import DisplayPrice from './components/project/display-price'
import Fanreel from './components/project/fanreel'
import FanreelOverlay from './components/project/fanreel-overlay'
import Footer from './components/project/footer'
import FooterContent from './components/project/footer-content'
import FooterSection from './components/project/footer-section'
import IconRow from './components/project/icon-row'
import ItemVariantImage from './components/project/item-variant-image'
import ItemVariantPrice from './components/project/item-variant-price'
import LinksList from './components/project/links-list'
import NestedAccordion from './components/project/nested-accordion'
import OfflineBanner from './components/project/offline-banner'
import Pagination from './components/project/pagination'
import ProductTile from './components/project/product-tile'
import ProductSetTile from './components/project/product-set-tile'
import ProductView from './components/project/product-view'
import RecommendedProducts from './components/project/recommended-products'
import SocialIcons from './components/project/social-icons'
import SwatchGroup from './components/project/swatch-group'
import ImageGallery from './components/project/image-gallery'
import Toast from './components/project/toast'
import ToggleCard from './components/project/toggle-card'
import ProductScroller from './components/project/product-scroller'
import Specifications from './components/project/specifications'
import AfterSales from './components/project/after-sales'
import Slider from './components/project/slider'
import PageNotFound from './components/project/page-not-found'
import ReviewOverview from './components/project/review-overview'
import RatingsBreakdown from './components/project/ratings-brakedown'
import AddReviews from './components/project/add-reviews'
import Review from './components/project/review'
import EmptyResults from './components/project/empty-results'
import CartDelivery from './components/project/cart-delivery'
import ProductVRLinks from './components/project/product-vr-links'
import Stores from './components/project/stores'
import TeaserLink from './components/project/teaser-link'
import WishlistToggle from './components/project/wishlist-toggle'

// Please refer to the Chakra-Ui theme customization docs found
// here https://chakra-ui.com/docs/theming/customize-theme to learn
// more about extending and overriding themes for your project.
const overrides = {
    styles,
    layerStyles,
    colors,
    sizes,
    space,
    gradients,
    shadows,
    textStyles,
    ...typography,
    breakpoints,
    animations,
    components: {
        // base components
        Accordion,
        Alert,
        Badge,
        Button,
        Checkbox,
        CloseButton,
        Container,
        Divider,
        Drawer,
        Form,
        FormError,
        FormLabel,
        Icon,
        Input,
        Textarea,
        Link,
        List,
        Menu,
        Modal,
        Popover,
        Progress,
        Radio,
        Select,
        Skeleton,
        SkipLink,
        Stepper,
        Tabs,
        Tooltip,
        Video,

        // project components
        Adyen,
        App,
        Chip,
        ColorsIndicator,
        DisplayPrice,
        Fanreel,
        FanreelOverlay,
        Footer,
        FooterContent,
        FooterSection,
        IconRow,
        ItemVariantImage,
        ItemVariantPrice,
        LinksList,
        NestedAccordion,
        OfflineBanner,
        SocialIcons,
        Pagination,
        ProductTile,
        ProductSetTile,
        ProductView,
        RecommendedProducts,
        SwatchGroup,
        ImageGallery,
        Toast,
        ToggleCard,
        ProductScroller,
        Specifications,
        AfterSales,
        Slider,
        PageNotFound,
        ReviewOverview,
        AddReviews,
        Review,
        RatingsBreakdown,
        EmptyResults,
        CartDelivery,
        ProductVRLinks,
        Stores,
        TeaserLink,
        WishlistToggle
    }
}

export default extendTheme(overrides)
